import React, { ReactElement, useEffect } from 'react';
import { useDeleteEntite, useEntites, useGetQuery } from '../../../Utils/api';
import { CellProps } from 'react-table';
import { Colonne, EtatDeTable } from '../Utils/TypesTableSimple';
import { Activite, TypeActivite } from '../../../../../common/src/entities';
import { Badge, Button, ButtonGroup, Col, Dropdown, Row } from 'react-bootstrap';
import { BiClipboard, BiDollarCircle, BiFile, BiNote, BiPlus } from 'react-icons/all';
import FiltreActif from '../Utils/FiltreActif';
import EnteteDePage from '../Utils/EnteteDePage';
import { LinkContainer } from 'react-router-bootstrap';
import PopoverSimple from '../../Common/PopoverSimple';
import { CelluleContact } from '../Utils/CelluleContact';
import { DateTime } from 'luxon';
import { capitalize } from '../../../Utils/stringUtils';
import { CopierPressePapier } from '../Utils/CopierPressePapier';
import * as clipboard from 'clipboard-polyfill';
import { useLoadPdf } from '../../Vitrine/Activites/PDF/LoadPDF';
import TableSimpleActivite from './TableSimpleActivite';
import axios from 'axios';
import { useRecoilState } from 'recoil';
import { etatMasquerCovid19 } from '../../../State';
import { QueryResult } from 'react-query';
import { FetchMoreOptions, RefetchOptions } from 'react-query/types/core/query';

const CHARGEMENT = 'Chargement...';
const TELECHAR_PDF = 'Télécharger le PDF';
const OUVRIR_PAGE = "Ouvrir la page de l'activité";
const MODIFIER = 'Modifier';
const AJOUTER_ACTIVITE = ' Ajouter une activité';
const EXPORTER = ' Exporter en CSV';
const SUPPRIMER = 'Supprimer';
export interface PropsDashboardActivites {
    mode: boolean;
}

export default function Activites(props: PropsDashboardActivites): ReactElement {
    const { mode } = props;
    const activites = useGetQuery<Activite[]>('activites/dashboard');

    useEffect(() => {
        document.title = 'Activités - Dashboard';
    }, []);

    async function downloadExcel() {
        // Envoyer la demande GET à l'API.
        const response = await axios.get('/api/activites/export-activites-csv', { responseType: 'arraybuffer' });

        // Créer un lien de téléchargement pour le fichier Excel.
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'activites.csv');
        document.body.appendChild(link);
        link.click();
    }

    return (
        <>
            <Row>
                <Col>
                    <EnteteDePage texte="Activités" cacherBoutonRetour />
                </Col>
                <Col xs="auto">
                    <ButtonGroup>
                        <LinkContainer to="new">
                            <Button size="sm" variant="outline-primary" className="d-flex align-items-center">
                                <BiPlus />
                                {AJOUTER_ACTIVITE}
                            </Button>
                        </LinkContainer>
                        <Button
                            size="sm"
                            variant="outline-secondary"
                            className="d-flex align-items-center"
                            onClick={downloadExcel}
                        >
                            <BiFile />
                            {EXPORTER}
                        </Button>
                    </ButtonGroup>
                </Col>
            </Row>
            <TableSimpleActivite
                colonnes={colonnesActivite}
                resultat={activites}
                lien="id"
                hover
                affichageMenu={innerMenu}
                affichageFiltres={FiltreActif}
                etatInitial={etatInitial}
                modePublic={mode}
            />
        </>
    );
}

/**
 * Affiche les options quand on appuie sur [...] à droite d'une rangée. Utiliser
 * InnerMenu comme un component React au lieu d'une fonction puisqu'il a besoin de hooks.
 * @param value Activité liée à la rangée dans la table
 * @returns l'intérieur du popup
 */
export function innerMenu(value: Activite): ReactElement {
    return <InnerMenu value={value} />;
}

interface PropsInnerMenu {
    value: Activite;
}

/**
 * Contenu du menu qui apparait quand on appuie sur [...] à la droite
 * d'une rangée de la table.
 * @param props contient l'activité liée à la rangée de la table
 * @returns l'intérieur du popup
 */
function InnerMenu(props: PropsInnerMenu): ReactElement {
    const [deleteActivite] = useDeleteEntite(Activite);
    const [masquerCovid19, setMasquerCovid19] = useRecoilState(etatMasquerCovid19);

    const { name, url, loading } = useLoadPdf(props.value, masquerCovid19.estMasquer);

    return (
        <>
            <LinkContainer to={String(props.value.id)}>
                <Dropdown.Item>{MODIFIER}</Dropdown.Item>
            </LinkContainer>
            <Dropdown.Item href={`/activites/${String(props.value.id)}`} target="_blank">
                {OUVRIR_PAGE}
            </Dropdown.Item>
            <Dropdown.Item href={url} disabled={loading} download={name}>
                {loading ? CHARGEMENT : TELECHAR_PDF}
            </Dropdown.Item>
            <Dropdown.Item className="text-danger" onClick={() => deleteActivite(props.value.id)}>
                {SUPPRIMER}
            </Dropdown.Item>
        </>
    );
}

export const etatInitial: Partial<EtatDeTable<Activite>> = {
    filters: [
        {
            id: 'actif',
            value: true,
        },
    ],
    sortBy: [
        {
            id: 'priorite',
            desc: true,
        },
    ],
    hiddenColumns: ['actif'],
};

export const colonnesActivite: Colonne<Activite>[] = [
    {
        Header: 'Actif',
        accessor: 'actif',
        Cell: function CelluleSuivi({ value }: CellProps<Activite>) {
            return value ? <Badge variant="light">Actif</Badge> : <Badge variant="secondary">Inactif</Badge>;
        },
        filter: 'equals',
    },
    {
        Header: 'Nom',
        accessor: 'name',
    },
    {
        Header: 'Notes',
        Cell: function CelluleNotes({ value }: CellProps<Activite>) {
            return value ? (
                <PopoverSimple inList texte={value} titre="Notes">
                    <BiNote />
                </PopoverSimple>
            ) : null;
        },
        accessor: 'notes',
        disableSortBy: true,
    },
    {
        Header: 'Priorité',
        accessor: 'priorite',
    },
    {
        Header: 'Type',
        accessor: 'typeActivite',
        Cell: function CelluleType({ value }: CellProps<TypeActivite>) {
            return value ?? null;
        },
        sortType: (rowA, rowB) => {
            const typeA = rowA.original.typeActivite.id;
            const typeB = rowB.original.typeActivite.id;
            if (typeA == null) return 1;
            if (typeB == null) return -1;
            return typeA >= typeB ? 1 : -1;
        },
    },
    {
        Header: 'Suivi',
        Cell: function CelluleSuivi({ value }: CellProps<Activite>) {
            if (value) {
                const date = DateTime.fromISO(value);
                const isPast = date.diffNow('days').days < 0;
                return (
                    <Badge variant={isPast ? 'warning' : 'secondary'}>
                        {isPast ? `Dû ${date.toRelativeCalendar()}` : capitalize(date.toRelativeCalendar())}
                    </Badge>
                );
            }
            return <Badge variant="success">OK</Badge>;
        },
        accessor: 'trigger_suivi',
        sortType: (rowA, rowB) => {
            const dateA = rowA.original.trigger_suivi ? DateTime.fromISO(rowA.original.trigger_suivi) : null;
            const dateB = rowB.original.trigger_suivi ? DateTime.fromISO(rowB.original.trigger_suivi) : null;
            if (dateA == null) return 1;
            if (dateB == null) return -1;
            return dateA.diff(dateB, 'days').days > 0 ? 1 : -1;
        },
    },
    {
        Header: 'Contact',
        accessor: 'contact_par_defaut',
        Cell: CelluleContact,
    },
    {
        accessor: 'id',
        Cell: function CelluleShortlist({ row: { original: value } }: CellProps<Activite>) {
            if (!value) return null;
            const tarifs = value.tarifs_estimes ? value.tarifs_estimes.replace(/[\n\r]/g, ' ') : '';
            const url = `${window.location.origin.toString()}/activites/${String(value.id)}`;
            const texte = `${value.name} - <a href="${url}">${url}</a>`;
            const stripTags = (t: string) => t.replace(/(<([^>]+)>)/gi, '');
            const texteTarifs = `${texte} - ${tarifs}`;
            return (
                <div className="d-flex">
                    <span>
                        <CopierPressePapier
                            texte={
                                new clipboard.ClipboardItem({
                                    'text/html': `<span>${texte}</span>`,
                                    'text/plain': stripTags(texte),
                                })
                            }
                        >
                            <PopoverSimple inList texte={stripTags(texte)} titre="Soumission">
                                <BiClipboard />
                            </PopoverSimple>
                        </CopierPressePapier>
                        <CopierPressePapier
                            texte={
                                new clipboard.ClipboardItem({
                                    'text/html': `<span>${texteTarifs}</span>`,
                                    'text/plain': stripTags(texteTarifs),
                                })
                            }
                        >
                            <PopoverSimple inList texte={stripTags(texteTarifs)} titre="Soumission + Prix">
                                <BiDollarCircle />
                            </PopoverSimple>
                        </CopierPressePapier>
                    </span>
                </div>
            );
        },
    },
];
